import { HistoryOutlined } from '@ant-design/icons';
import JqFieldHistoryModal from '@zeus/core/modals/JqFieldHistoryModal';
import { ICallApi } from '_/api/history/fieldVersions.api';
import useToggleValue from '_/hooks/useToggleValue';
import { Button, Input, Space, Tooltip } from 'antd';
import React, { ReactElement } from 'react';

const { TextArea } = Input;

export interface IJqFieldHistorySource {
  api: ICallApi & { objectName?: string };
  childPath?: string;
  fieldPath: string;
  resourceId?: string;
}

interface IJqExpressionFieldProps {
  disabled?: boolean;
  historySource?: IJqFieldHistorySource;
  id?: string;
  // Extra native attributes (name, aria-*, data-test-id, ...) forwarded as-is to the
  // underlying Input/TextArea.
  inputProps?: Record<string, unknown>;
  label?: string;
  onBlur?: () => void;
  onChange: (value: string) => void;
  // Called after a successful Restore, in addition to onChange -- defaults to onChange.
  // Pass a separate handler for fields that persist on blur (a programmatic restore never
  // fires a blur event), so restoring can still save immediately.
  onRestore?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  value: string;
}

// Drop-in replacement for a plain antd Input/Input.TextArea for a jq expression field,
// adding a History icon that opens a popup with the field's version history and a Restore
// action (see JqFieldHistoryModal). Stays fully controlled (value/onChange) so it slots into
// an antd Form.Item, a save-on-blur field, or a wizard's own local state unchanged -- only
// persisting the restored value is up to the caller, same as any other edit to this field.
const JqExpressionField = ({
  disabled = false,
  historySource,
  id,
  inputProps,
  label,
  onBlur,
  onChange,
  onRestore,
  placeholder,
  rows,
  value,
}: IJqExpressionFieldProps): ReactElement => {
  const [historyOpen, toggleHistoryOpen] = useToggleValue();

  const canShowHistory = !!historySource?.resourceId;

  const handleRestored = (restoredValue: string): void => {
    (onRestore || onChange)(restoredValue);
    toggleHistoryOpen();
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {(label || canShowHistory) && (
        <Space size={4}>
          {label && <span>{label}</span>}
          {canShowHistory && (
            <Tooltip title="View field history">
              <Button type="text" size="small" shape="circle" icon={<HistoryOutlined />} onClick={toggleHistoryOpen} />
            </Tooltip>
          )}
        </Space>
      )}
      {rows && rows > 1 ? (
        <TextArea
          id={id}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          {...inputProps}
        />
      ) : (
        <Input
          id={id}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          {...inputProps}
        />
      )}
      {canShowHistory && (
        <JqFieldHistoryModal
          open={historyOpen}
          onCancel={toggleHistoryOpen}
          onRestored={handleRestored}
          api={historySource!.api}
          resourceId={historySource!.resourceId}
          childPath={historySource!.childPath}
          fieldPath={historySource!.fieldPath}
          currentValue={value}
          label={label}
        />
      )}
    </Space>
  );
};

export default JqExpressionField;
